import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../../config/api';
import API_CONFIG from '../../config/api';

// Async thunk to create a client (Step 1)
export const createClient = createAsyncThunk(
    'onboarding/createClient',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiClient.post(
                API_CONFIG.ENDPOINTS.ONBOARDING_CLIENT,
                data
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to create client';
            return rejectWithValue(message);
        }
    }
);

// Async thunk to create a workspace (Step 2)
export const createWorkspace = createAsyncThunk(
    'onboarding/createWorkspace',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiClient.post(
                API_CONFIG.ENDPOINTS.ONBOARDING_WORKSPACE,
                data
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to create workspace';
            return rejectWithValue(message);
        }
    }
);

// Async thunk to create an asset (Step 3)
export const createAsset = createAsyncThunk(
    'onboarding/createAsset',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiClient.post(
                API_CONFIG.ENDPOINTS.ONBOARDING_ASSET,
                data
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to create asset';
            return rejectWithValue(message);
        }
    }
);

// Async thunk to fetch all clients
export const fetchClients = createAsyncThunk(
    'onboarding/fetchClients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(
                API_CONFIG.ENDPOINTS.ONBOARDING_CLIENTS
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch clients';
            return rejectWithValue(message);
        }
    }
);



// Async thunk to update client status
export const updateClientStatus = createAsyncThunk(
    'onboarding/updateClientStatus',
    async ({ clientUUID, status }, { rejectWithValue }) => {
        try {
            const response = await apiClient.patch(
                API_CONFIG.ENDPOINTS.ONBOARDING_CLIENT_STATUS(clientUUID),
                status,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            return { clientUUID, status };
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to update client status';
            return rejectWithValue(message);
        }
    }
);

// Helper to get selected client from localStorage
const getStoredSelectedClientId = () => {
    try {
        return localStorage.getItem('selectedClientId') || null;
    } catch {
        return null;
    }
};

const initialState = {
    // Current step in the multi-step form
    currentStep: 1,

    // Loading states
    loading: false,
    error: null,

    // Created entities
    createdClient: null,
    createdWorkspaces: [],
    createdAsset: null,

    // Clients list
    clients: [],
    clientsStatus: 'idle',
    clientsError: null,

    // Selected client for global filtering (restored from localStorage)
    selectedClientId: getStoredSelectedClientId(),

    // Individual operation states
    createClientStatus: 'idle',
    createWorkspaceStatus: 'idle',
    createAssetStatus: 'idle'
};

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        setStep: (state, action) => {
            state.currentStep = action.payload;
        },
        setSelectedClientId: (state, action) => {
            state.selectedClientId = action.payload;
            // Persist to localStorage
            try {
                if (action.payload) {
                    localStorage.setItem('selectedClientId', action.payload);
                } else {
                    localStorage.removeItem('selectedClientId');
                }
            } catch {
                // Ignore localStorage errors
            }
        },
        resetOnboarding: (state) => {
            state.currentStep = 1;
            state.loading = false;
            state.error = null;
            state.createdClient = null;
            state.createdWorkspaces = [];
            state.createdAsset = null;
            state.createClientStatus = 'idle';
            state.createWorkspaceStatus = 'idle';
            state.createAssetStatus = 'idle';
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Client
            .addCase(createClient.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.createClientStatus = 'loading';
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.loading = false;
                state.createdClient = action.payload;
                state.createClientStatus = 'succeeded';
                state.currentStep = 2;
            })
            .addCase(createClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create client';
                state.createClientStatus = 'failed';
            })

            // Create Workspace
            .addCase(createWorkspace.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.createWorkspaceStatus = 'loading';
            })
            .addCase(createWorkspace.fulfilled, (state, action) => {
                state.loading = false;
                state.createdWorkspaces.push(action.payload);
                state.createWorkspaceStatus = 'succeeded';
            })
            .addCase(createWorkspace.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create workspace';
                state.createWorkspaceStatus = 'failed';
            })

            // Create Asset
            .addCase(createAsset.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.createAssetStatus = 'loading';
            })
            .addCase(createAsset.fulfilled, (state, action) => {
                state.loading = false;
                state.createdAsset = action.payload;
                state.createAssetStatus = 'succeeded';
            })
            .addCase(createAsset.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create asset';
                state.createAssetStatus = 'failed';
            })

            // Fetch Clients
            .addCase(fetchClients.pending, (state) => {
                state.clientsStatus = 'loading';
                state.clientsError = null;
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.clientsStatus = 'succeeded';
                state.clients = action.payload || [];
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.clientsStatus = 'failed';
                state.clientsError = action.payload || 'Failed to fetch clients';
            })

            // Update Client Status
            .addCase(updateClientStatus.fulfilled, (state, action) => {
                const { clientUUID, status } = action.payload;
                const clientIndex = state.clients.findIndex(
                    (client) => client.clientUUID === clientUUID
                );
                if (clientIndex !== -1) {
                    state.clients[clientIndex].status = status;
                    // Optionally update modifiedAt if the API doesn't return the full object
                    state.clients[clientIndex].modifiedAt = new Date().toISOString();
                }
            });
    }
});

export const { setStep, setSelectedClientId, resetOnboarding, clearError } = onboardingSlice.actions;

export default onboardingSlice.reducer;
